/**
 * Notification hooks —— 收件箱 / 未读数 / 偏好的 TanStack Query 封装，
 * 以及 /notifications/stream SSE 连接管理（useNotificationStream）。
 *
 * SSE 契约（app/modules/notification/sse.py）：
 *  - `connected`     连接建立（每次成功连上都会发，含重连）
 *  - `notification`  一条新通知（载荷为该收件箱行）
 *  - `ping`          15s 心跳，仅保活
 * 收件箱接口是唯一事实源：事件到达只做 invalidate，由查询重新拉取，
 * 断线期间漏掉的通知靠重连后的对账补齐。
 *
 * 断线降级策略：连续 N 次连不上（服务端停转/网络中断）→ 切 60s 轮询对账
 * （未读数 + 收件箱首屏，由调用方把 mode 折算成 refetchInterval 注入查询）；
 * 之后仍按分钟级节奏重试 SSE，恢复（再次收到 connected）后自动切回。
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "../api/notifications";
import { startSseLoop, type SseLoopHandle } from "../api/sse";
import type { NotificationDigestFrequency } from "../types/notification";

/** query key 工厂（invalidate 一律以 ["notifications", ...] 前缀为目标）。 */
export const notificationQueryKeys = {
  inbox: (params?: Record<string, unknown>) => ["notifications", "inbox", params] as const,
  unread: ["notifications", "unread"] as const,
  prefs: ["notifications", "prefs"] as const,
  topics: ["notifications", "topics"] as const,
};

// --- SSE 连接参数（具名常量，测试通过较短的实际等待或 fake timers 驱动） ---

const SSE_STREAM_PATH = "/api/v1/notifications/stream";
/** 连续建连失败上限，超过即降级轮询。 */
const SSE_MAX_CONSECUTIVE_FAILURES = 5;
/** 建连失败后的重试间隔。 */
const SSE_RECONNECT_DELAY_MS = 3_000;
/** 降级轮询期间 SSE 的探测间隔（与轮询对账同节奏）。 */
const SSE_POLLING_RETRY_DELAY_MS = 60_000;
/** 轮询对账间隔（未读数 + 收件箱首屏）。 */
export const NOTIFICATION_POLL_INTERVAL_MS = 60_000;

export type NotificationStreamMode = "connecting" | "sse" | "polling";

/** SSE 断线降级后的轮询间隔；SSE 健在时不轮询。 */
export function pollIntervalForMode(mode: NotificationStreamMode): number | false {
  return mode === "polling" ? NOTIFICATION_POLL_INTERVAL_MS : false;
}

/**
 * 建立并维护通知 SSE 连接：
 *  - `notification` 事件 / 断线恢复的 `connected` 事件 → invalidate inbox + unread；
 *  - 连续失败超上限 → mode="polling"（调用方注入 60s refetchInterval）；
 *  - 降级后仍周期探测，恢复 → mode="sse"。
 * 每个挂载点一条连接（通知铃铛是全局唯一挂载点，在顶栏常驻）。
 *
 * 连接调度（建连判定 / 失败累计 / 降级探测）复用 `startSseLoop`（切片 2 从
 * 本 hook 下沉至 api/sse.ts 的公共实现），这里只保留通知域语义：connected 帧
 * 恢复对账、notification 帧 invalidate、降级态切换。
 */
export function useNotificationStream(): { mode: NotificationStreamMode } {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<NotificationStreamMode>("connecting");

  useEffect(() => {
    let droppedSinceLastConnected = false;

    const invalidateNotificationData = () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    };

    const loop: SseLoopHandle = startSseLoop({
      url: () => SSE_STREAM_PATH,
      establishmentEvent: "connected",
      onFrame: (eventType) => {
        if (eventType === "connected") {
          const isRecovery = droppedSinceLastConnected;
          droppedSinceLastConnected = false;
          setMode("sse");
          if (isRecovery) invalidateNotificationData();
        } else if (eventType === "notification") {
          invalidateNotificationData();
        }
        // ping：心跳，无需处理。
      },
      onDisconnect: () => {
        droppedSinceLastConnected = true;
      },
      onDegraded: () => {
        setMode("polling");
      },
      maxConsecutiveFailures: SSE_MAX_CONSECUTIVE_FAILURES,
      reconnectDelayMs: SSE_RECONNECT_DELAY_MS,
      degradedRetryDelayMs: SSE_POLLING_RETRY_DELAY_MS,
    });

    return () => loop.stop();
  }, [queryClient]);

  return { mode };
}

// ---------------------------------------------------------------------------
// 收件箱查询与已读变更
// ---------------------------------------------------------------------------

export function useNotificationInbox(
  params?: { page?: number; pageSize?: number },
  options?: { refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: notificationQueryKeys.inbox(params),
    queryFn: () => notificationsApi.listInbox(params),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useUnreadNotificationCount(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: notificationQueryKeys.unread,
    queryFn: () => notificationsApi.getUnreadCount().then((res) => res.data.unreadCount),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inboxId: string) => notificationsApi.markRead(inboxId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread"] });
    },
  });
}

// ---------------------------------------------------------------------------
// 偏好与主题
// ---------------------------------------------------------------------------

export function useNotificationTopics() {
  return useQuery({
    queryKey: notificationQueryKeys.topics,
    queryFn: () => notificationsApi.listTopics().then((res) => res.data),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationQueryKeys.prefs,
    queryFn: () => notificationsApi.getPreferences().then((res) => res.data),
  });
}

export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { topicCode: string; channel: string; enabled: boolean }) =>
      notificationsApi.setPreference(args),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "prefs"] });
    },
  });
}

export function useSetNotificationDigestFrequency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (frequency: NotificationDigestFrequency) =>
      notificationsApi.setDigestFrequency(frequency),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "prefs"] });
    },
  });
}
